import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { CircleDashed, Pencil, Plus, Target, Trophy } from 'lucide-react';
import { api } from '../../api/client';
import { PageHeaderBand } from '../../components/layout/PageHeaderBand';
import { CustomerStatusBadge } from '../../components/customers/CustomerStatusBadge';
import { OpportunityStageBadge } from '../../components/sales/OpportunityStageBadge';
import { OpportunityFormDialog } from '../../components/sales/OpportunityFormDialog';
import { LoadFailureNotice } from '../../components/ui/LoadFailureNotice';
import { useAuth } from '../../contexts/AuthContext';
import { useLocale } from '../../contexts/LocaleContext';
import { useTenantTimeZone } from '../../hooks/useTenantTimeZone';
import { useUrlState } from '../../hooks/useUrlState';
import { unwrapArray } from '../../lib/api-envelope';
import { formatCalendarDate } from '../../lib/calendar-date';
import { fmtCurrency } from '../../lib/display-labels';
import { LOAD_FAILURE_QUERY_OPTIONS, queryFailureError, queryLoadFailure } from '../../lib/load-failure';
import { canChangeOpportunity, nextStepOverdue, STAGE_LABELS, stageSkin } from '../../lib/opportunity';
import { OPEN_STAGES, type Opportunity, type OpportunityStage, type Pipeline, type PipelineStage } from '../../types/opportunity';

/**
 * /sales/pipeline — the opportunity register (CRM-FE-3). A register with stage
 * chips, not a kanban.
 *
 * EVERY FIGURE IS THE API'S. GET /opportunities/pipeline returns, per stage,
 * `count`, `valueKnownTotal` and `valueUnknownCount`, and NO total across
 * stages. The band therefore shows each open stage on its own — its count and
 * its known value as sent — and never adds money across stages in the browser
 * (operator, 2026-10-14: "per stage, no total"). The fourth stat, Unvalued, is
 * the API's per-stage unvalued counts side by side for the same reason: an
 * opportunity with no estimate is never folded into a value, and never a $0.
 *
 * Chips: Open (lead, qualified, quoted — the default, no count of its own,
 * since the API returns none) and one per stage, each carrying its API count.
 *
 * WEIGHTED VALUE PER STAGE is the API's `weightedValueKnownTotal` (Σ value ×
 * probability over KNOWN values), shown in the body's stage strip beside the
 * same stage's known value and unvalued count — never re-added from rows, and
 * never across stages. The band is unchanged: its stat text is what the hero
 * circle's clearance was measured against.
 *
 * OWNER FILTER is the API's `owner` param on BOTH the list and /pipeline, so
 * the chips' counts and the stage strip answer for the same owner as the rows.
 * It lives in the URL (`?owner=`), as the stage chip does.
 *
 * OVERDUE NEXT STEP chip: open deals whose next-step due day is before the
 * tenant's today (lib/opportunity nextStepOverdue). No endpoint returns this
 * set or its count, so it is the OPEN list (one GET, unpaginated up to the
 * API's LIMIT 500) narrowed here, and its count is a tally of those loaded
 * rows — honest because the list is the whole open set below 500; at 500 the
 * count reads "500+" rather than claim a total it cannot see.
 *
 * ROWS HOLD AN INLINE EDIT BUTTON, so this is the Rate Sheets exception: no
 * `role="button"` on the row, and Enter/Space open the row only when the row
 * itself has focus.
 */
type ChipKey = 'open' | 'overdue' | OpportunityStage;
const CHIPS: ChipKey[] = ['open', 'overdue', 'lead', 'qualified', 'quoted', 'won', 'lost'];
const chipLabel = (k: ChipKey) => (k === 'open' ? 'Open' : k === 'overdue' ? 'Overdue next step' : STAGE_LABELS[k]);
// The overdue chip reads the open list and narrows it; it shares the open chip's query.
const stageParam = (k: ChipKey) => (k === 'open' || k === 'overdue' ? OPEN_STAGES.join(',') : k);
const listKey = (k: ChipKey) => (k === 'overdue' ? 'open' : k);
const API_LIST_LIMIT = 500;

interface OwnerRow { id: string; fullName?: string | null; email?: string | null; role?: string | null }
const OWNER_ROLES = new Set(['admin', 'office_staff', 'supervisor', 'dispatcher', 'sales']);

function StageFigure({ s, currency }: { s: PipelineStage | undefined; currency: string | null }) {
  if (!s) return <>—</>;
  return (
    <span className="inline-flex items-baseline gap-2">
      <span>{s.count}</span>
      <span className="text-sm font-semibold">{fmtCurrency(s.valueKnownTotal, currency)}</span>
    </span>
  );
}

export default function PipelineScreen() {
  const navigate = useNavigate();
  const { user } = useAuth();
  // The pipeline carries no currency; its values are in the tenant's (companies.currency).
  const { currencyCode } = useLocale();
  const [chipParam, setChip] = useUrlState<string>('stage', 'open');
  const chip: ChipKey = (CHIPS as string[]).includes(chipParam) ? (chipParam as ChipKey) : 'open';
  const [ownerParam, setOwner] = useUrlState<string>('owner', '');
  const owner = ownerParam || undefined;
  const timeZone = useTenantTimeZone();
  const [editing, setEditing] = useState<Opportunity | null>(null);
  const [creating, setCreating] = useState(false);

  const pipelineQuery = useQuery({
    queryKey: ['opportunities', 'pipeline', { owner: owner ?? null }],
    queryFn: () => api.opportunities.pipeline({ include_closed: 1, ...(owner ? { owner } : {}) }).then((r) => r.data.data as Pipeline),
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
  const pipelineFailure = queryLoadFailure(pipelineQuery);
  const pipeline = pipelineFailure ? undefined : pipelineQuery.data;
  const stageOf = (k: string) => pipeline?.stages.find((s) => s.stage === k);
  const pipelineLoading = !pipelineFailure && pipelineQuery.data === undefined;

  const listFor = (k: ChipKey) => ({
    queryKey: ['opportunities', 'list', listKey(k), { owner: owner ?? null }],
    queryFn: () => api.opportunities.list({ stage: stageParam(k), ...(owner ? { owner } : {}) }).then((r) => unwrapArray<Opportunity>(r.data)),
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
  const listQuery = useQuery(listFor(chip));
  // The open list, for the overdue chip's count (the same query when the open or overdue chip is active).
  const openQuery = useQuery(listFor('open'));
  const failure = queryLoadFailure(listQuery);
  const isOverdue = (o: Opportunity) => nextStepOverdue(o, timeZone) === true;
  const loaded = failure ? undefined : listQuery.data;
  const rows = loaded && chip === 'overdue' ? loaded.filter(isOverdue) : loaded;
  const openRows = queryLoadFailure(openQuery) ? undefined : openQuery.data;
  const overdueCount = openRows
    ? `${openRows.filter(isOverdue).length}${openRows.length >= API_LIST_LIMIT ? '+' : ''}`
    : undefined;

  const usersQuery = useQuery({
    queryKey: ['users', 'list', 'opportunity-owner'],
    queryFn: () => api.users.list().then((r) => unwrapArray<OwnerRow>(r.data)),
    staleTime: 5 * 60_000,
  });
  const owners = useMemo(
    () => [...(usersQuery.data ?? []).filter((u) => OWNER_ROLES.has(String(u.role)))]
      .sort((a, b) => (a.fullName ?? '').localeCompare(b.fullName ?? '')),
    [usersQuery.data],
  );

  const open = (id: string) => navigate(`/sales/pipeline/${id}`);
  const rowKeys = (id: string) => (event: React.KeyboardEvent<HTMLTableRowElement>) => {
    if (event.target !== event.currentTarget) return; // a key on an inner button is the button's
    if (event.key === 'Enter' || event.key === ' ') { event.preventDefault(); open(id); }
  };

  const quoteCell = (o: Opportunity) => {
    const q = o.latestQuote;
    if (!q) return <span className="text-fieldiq-muted">—</span>;
    return (
      <span className="text-fieldiq-text">
        {[q.quoteNumber ?? '—', q.status, fmtCurrency(q.totalAmount, q.currency)].join(' · ')}
      </span>
    );
  };

  const nextStepCell = (o: Opportunity) => {
    if (!o.nextStep && !o.nextStepDue) return <span className="text-fieldiq-muted">—</span>;
    const overdue = nextStepOverdue(o, timeZone) === true;
    return (
      <span className="block">
        <span className="block truncate text-fieldiq-text">{o.nextStep ?? '—'}</span>
        <span className="mt-0.5 inline-flex items-center gap-2 text-xs text-fieldiq-muted">
          Due {formatCalendarDate(o.nextStepDue) ?? '—'}
          {overdue && (
            <span className="rounded-fi-pill border border-black/20 bg-fi-signal-red px-2 py-0.5 text-[0.62rem] font-black uppercase leading-none tracking-[0.06em] text-fi-signal-ink">Overdue</span>
          )}
        </span>
      </span>
    );
  };

  return (
    <div data-testid="pipeline-page" className="animate-fade-in min-h-full bg-fieldiq-dark">
      <PageHeaderBand
        eyebrow="Sales"
        title="Pipeline"
        onBack={() => navigate(-1)}
        backLabel="Back"
        actions={
          <button type="button" onClick={() => setCreating(true)} className="fi-action fi-action--standard fi-action--primary">
            <Plus className="size-4" aria-hidden="true" />
            New opportunity
          </button>
        }
        stats={[
          { icon: <CircleDashed className="size-4" aria-hidden="true" />, label: 'Lead', sub: 'Count · known value', tone: 'neutral', loading: pipelineLoading, value: <StageFigure s={stageOf('lead')} currency={currencyCode} /> },
          { icon: <Target className="size-4" aria-hidden="true" />, label: 'Qualified', sub: 'Count · known value', tone: 'info', loading: pipelineLoading, value: <StageFigure s={stageOf('qualified')} currency={currencyCode} /> },
          { icon: <Trophy className="size-4" aria-hidden="true" />, label: 'Quoted', sub: 'Count · known value', tone: 'accent', loading: pipelineLoading, value: <StageFigure s={stageOf('quoted')} currency={currencyCode} /> },
          {
            icon: <CircleDashed className="size-4" aria-hidden="true" />,
            label: 'Unvalued',
            sub: 'Lead / qual. / quoted',
            tone: 'neutral',
            loading: pipelineLoading,
            value: pipeline
              ? OPEN_STAGES.map((k) => stageOf(k)?.valueUnknownCount ?? '—').join(' / ')
              : '—',
          },
        ]}
      />

      <section className="px-[var(--fi-page-inline)] py-[var(--fi-page-block)] pb-[var(--bottom-chrome)]">
        <div className="mx-auto max-w-[1920px] space-y-4">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3" aria-label="Open stages">
            {OPEN_STAGES.map((k) => {
              const st = stageOf(k);
              return (
                <div key={k} data-testid={`stage-strip-${k}`} className={`rounded-fi-panel border border-fieldiq-border border-l-4 bg-fieldiq-card px-4 py-3 ${stageSkin(k).rail}`}>
                  <p className="text-xs font-black uppercase tracking-[0.14em] text-fieldiq-muted">
                    {STAGE_LABELS[k]}{st ? ` · ${st.defaultProbability}% default` : ''}
                  </p>
                  {pipelineLoading ? (
                    <div className="mt-2 h-10 animate-pulse rounded bg-fieldiq-dark/60" aria-busy="true" />
                  ) : (
                    <dl className="mt-1 grid grid-cols-2 gap-x-3 text-sm">
                      <dt className="text-fieldiq-muted">Weighted</dt>
                      <dd className="text-right font-extrabold tabular-nums text-white">{st ? fmtCurrency(st.weightedValueKnownTotal, currencyCode) : '—'}</dd>
                      <dt className="text-fieldiq-muted">Known value</dt>
                      <dd className="text-right tabular-nums text-fieldiq-text">{st ? fmtCurrency(st.valueKnownTotal, currencyCode) : '—'}</dd>
                      <dt className="text-fieldiq-muted">Deals · unvalued</dt>
                      <dd className="text-right tabular-nums text-fieldiq-text">{st ? `${st.count} · ${st.valueUnknownCount}` : '—'}</dd>
                    </dl>
                  )}
                </div>
              );
            })}
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <label htmlFor="pipeline-owner" className="text-xs font-semibold text-fieldiq-muted">Owner</label>
            <select
              id="pipeline-owner"
              value={ownerParam}
              onChange={(e) => setOwner(e.target.value)}
              className="min-h-[36px] rounded-fi-control border border-fieldiq-border bg-fieldiq-card px-3 text-sm text-fieldiq-text"
            >
              <option value="">Everyone</option>
              {user?.id && <option value={user.id}>Me</option>}
              {owners.filter((u) => u.id !== user?.id).map((u) => <option key={u.id} value={u.id}>{u.fullName ?? u.email ?? '—'}</option>)}
              {/* A shared link can name an owner this list does not hold. */}
              {ownerParam && ownerParam !== user?.id && !owners.some((u) => u.id === ownerParam) && <option value={ownerParam}>Selected owner</option>}
            </select>
          </div>

          <div className="scrollbar-hide flex gap-2 overflow-x-auto">
            {CHIPS.map((k) => {
              const active = chip === k;
              const count = k === 'open' ? undefined : k === 'overdue' ? overdueCount : stageOf(k)?.count;
              const countPending = k === 'overdue' ? overdueCount === undefined : pipelineLoading;
              return (
                <button
                  key={k}
                  type="button"
                  aria-pressed={active}
                  onClick={() => setChip(k)}
                  className={`inline-flex flex-shrink-0 items-center gap-1.5 whitespace-nowrap rounded-fi-pill px-4 py-1.5 text-xs font-extrabold transition ${
                    active ? 'bg-fi-amber text-fi-amber-ink' : 'border border-fieldiq-border bg-fieldiq-card text-fieldiq-muted hover:text-fieldiq-text'
                  }`}
                >
                  {chipLabel(k)}
                  {k !== 'open' && !countPending && (
                    <span className={`tabular-nums ${active ? 'text-fi-amber-ink' : 'text-fieldiq-muted'}`}>{count ?? '—'}</span>
                  )}
                </button>
              );
            })}
          </div>

          <section aria-labelledby="pipeline-register-title" className="overflow-hidden rounded-fi-panel border border-fieldiq-border border-l-4 border-l-fi-amber bg-fieldiq-card">
            <header className="flex items-end justify-between gap-3 border-b border-fieldiq-border px-4 py-4">
              <div>
                <p className="text-[0.65rem] font-black uppercase tracking-[0.14em] text-fieldiq-muted">Opportunity register</p>
                <h2 id="pipeline-register-title" className="mt-1 text-base font-extrabold tracking-tight text-white">
                  {chip === 'open' ? 'Open opportunities' : chip === 'overdue' ? 'Overdue next steps' : STAGE_LABELS[chip]}
                </h2>
              </div>
            </header>

            {failure ? (
              <LoadFailureNotice error={queryFailureError(listQuery)} subject="opportunities" />
            ) : rows === undefined ? (
              <div className="space-y-3 p-4" aria-busy="true">
                {[1, 2, 3].map((i) => <div key={i} className="h-12 animate-pulse rounded-fi-panel bg-fieldiq-dark/60" />)}
              </div>
            ) : rows.length === 0 ? (
              <p className="px-4 py-16 text-center text-sm text-fieldiq-muted">
                {chip === 'open' ? 'No open opportunities' : chip === 'overdue' ? 'No open deal has an overdue next step' : `No ${STAGE_LABELS[chip].toLowerCase()} opportunities`}
              </p>
            ) : (
              <>
                <div className="hidden overflow-x-auto md:block">
                  <table className="w-full min-w-[1400px] text-left">
                    <thead className="border-b border-fieldiq-border bg-fieldiq-dark/40">
                      <tr className="border-l-4 border-l-transparent">
                        {['Opportunity', 'Customer', 'Contact', 'Stage', 'Est. value', 'Next step', 'Latest quote', 'Expected close', 'Owner', ''].map((h, i) => (
                          <th key={i} className="whitespace-nowrap px-4 py-3.5 text-xs font-bold uppercase tracking-wider text-fieldiq-muted">{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map((o) => (
                        <tr
                          key={o.id}
                          tabIndex={0}
                          onClick={() => open(o.id)}
                          onKeyDown={rowKeys(o.id)}
                          className={`cursor-pointer border-l-4 border-t border-fieldiq-border text-sm outline-none transition-colors hover:bg-fieldiq-dark/50 focus-visible:bg-fi-amber/10 ${stageSkin(o.stage).rail}`}
                        >
                          <td className="px-4 py-3 font-extrabold text-white">{o.title}</td>
                          <td className="px-4 py-3">
                            <span className="inline-flex items-center gap-2">
                              <span className="text-fieldiq-text">{o.customerName ?? '—'}</span>
                              <CustomerStatusBadge status={o.customerStatus} />
                            </span>
                          </td>
                          <td className="px-4 py-3 text-fieldiq-text">{o.contactName ?? '—'}</td>
                          <td className="px-4 py-3"><OpportunityStageBadge stage={o.stage} /></td>
                          <td className="px-4 py-3 tabular-nums text-fieldiq-text">{fmtCurrency(o.estimatedValue, o.currency)}</td>
                          <td className="max-w-[280px] px-4 py-3">{nextStepCell(o)}</td>
                          <td className="px-4 py-3">{quoteCell(o)}</td>
                          <td className="px-4 py-3 text-fieldiq-text">{formatCalendarDate(o.expectedCloseDate) ?? '—'}</td>
                          <td className="px-4 py-3 text-fieldiq-text">{o.ownerName ?? '—'}</td>
                          <td className="px-3 py-3" onClick={(e) => e.stopPropagation()}>
                            {canChangeOpportunity(user, o) && (
                              <button
                                type="button"
                                onClick={() => setEditing(o)}
                                aria-label={`Edit ${o.title}`}
                                className="flex h-8 items-center gap-1 rounded-fi-control border border-fieldiq-border px-2.5 text-xs font-bold text-fieldiq-text hover:text-white"
                              >
                                <Pencil size={12} aria-hidden="true" /> Edit
                              </button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="space-y-3 p-4 md:hidden">
                  {rows.map((o) => (
                    <button key={o.id} type="button" onClick={() => open(o.id)} className="block w-full rounded-fi-panel border border-fieldiq-border bg-fieldiq-card p-4 text-left">
                      <div className="flex items-start justify-between gap-2">
                        <span className="min-w-0 text-sm font-extrabold text-white">{o.title}</span>
                        <OpportunityStageBadge stage={o.stage} />
                      </div>
                      <p className="mt-1 text-sm text-fieldiq-muted">{o.customerName ?? '—'} · {o.contactName ?? 'No contact'} · {o.ownerName ?? '—'}</p>
                      <div className="mt-2 text-sm">{nextStepCell(o)}</div>
                      <p className="mt-2 text-sm text-fieldiq-text">
                        {fmtCurrency(o.estimatedValue, o.currency)} · close {formatCalendarDate(o.expectedCloseDate) ?? '—'}
                      </p>
                    </button>
                  ))}
                </div>
              </>
            )}
          </section>
        </div>
      </section>

      <OpportunityFormDialog open={creating} onClose={() => setCreating(false)} onSaved={(row) => row?.id && open(row.id)} />
      <OpportunityFormDialog open={!!editing} existing={editing} onClose={() => setEditing(null)} />
    </div>
  );
}
